import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setMobileOpen(false);
    }
  };

  const toggleLang = (lang: string) => {
    i18n.changeLanguage(lang);
    setMobileOpen(false);
  };

  const currentLang = i18n.language || 'en';

  const navItems = [
    { label: t('nav.portfolio'), id: 'portfolio' },
    { label: t('nav.services'), id: 'services' },
    { label: t('nav.howWeWork'), id: 'howwework' },
    { label: t('nav.about'), id: 'about' },
    { label: t('nav.team'), id: 'team' },
    { label: t('nav.contact'), id: 'contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-dark-900/90 backdrop-blur-lg border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-12 py-4 flex items-center justify-between">
        <button
          onClick={() => scrollTo('hero')}
          className="flex items-center h-9 cursor-pointer"
        >
          <img
            src="https://storage.readdy-site.link/project_files/cda64c73-0cd6-4b91-8a57-da1fc108f4d8/c8da654f-8114-430e-9c9c-3079f9d57510_svgmaker-editor.svg?v=bda0da09cbc0c79cb977d66f891a46d3"
            alt="Quaron Software"
            className="h-full w-auto"
          />
        </button>

        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-sm text-gray-400 hover:text-neon-blue transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-4">
          <div className="flex items-center gap-1 p-1 rounded-full bg-dark-500/60 border border-white/5">
            <button
              onClick={() => toggleLang('en')}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer ${
                currentLang.startsWith('en')
                  ? 'bg-neon-blue text-dark-900'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              EN
            </button>
            <button
              onClick={() => toggleLang('uk')}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer ${
                currentLang.startsWith('uk')
                  ? 'bg-neon-blue text-dark-900'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              UA
            </button>
          </div>

          <button
            onClick={() => scrollTo('contact')}
            className="px-5 py-2.5 bg-neon-blue/10 border border-neon-blue/30 rounded-full text-sm text-neon-blue hover:bg-neon-blue/20 hover:border-neon-blue/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('nav.startProject')}
          </button>
        </div>

        <button
          className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-dark-900/95 backdrop-blur-lg border-t border-white/5 px-4 pb-6 pt-2">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="block w-full text-left py-3 text-sm text-gray-400 hover:text-neon-blue transition-colors duration-300 cursor-pointer"
            >
              {item.label}
            </button>
          ))}
          <div className="flex items-center gap-2 mt-4">
            <button
              onClick={() => toggleLang('en')}
              className={`flex-1 py-2 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer ${
                currentLang.startsWith('en')
                  ? 'bg-neon-blue text-dark-900'
                  : 'bg-dark-500 text-gray-400 hover:text-white'
              }`}
            >
              EN
            </button>
            <button
              onClick={() => toggleLang('uk')}
              className={`flex-1 py-2 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer ${
                currentLang.startsWith('uk')
                  ? 'bg-neon-blue text-dark-900'
                  : 'bg-dark-500 text-gray-400 hover:text-white'
              }`}
            >
              UA
            </button>
          </div>
          <button
            onClick={() => scrollTo('contact')}
            className="mt-4 w-full py-3 bg-neon-blue/10 border border-neon-blue/30 rounded-full text-sm text-neon-blue hover:bg-neon-blue/20 transition-all duration-300 cursor-pointer"
          >
            {t('nav.startProject')}
          </button>
        </div>
      )}
    </nav>
  );
}
